import { useEffect, useState } from "react";
import { City, Itinerary } from "../types";
import LoadingMessage from "./LoadingMessage";
import api from "../api";
import { apiRoutes } from "../routes/routes";
import { Card, Button, Row, Col } from "react-bootstrap";

interface Props {
  itinerary: Itinerary;
  cities: Record<number, City>;
}

interface ActivitySearch {
  id: number;
  location: City;
}

interface Activity {
  title: string;
  image: string | null;
  url: string;
  description: string;
  price: number;
  currency: string;
}

const ActivitySearchPanel: React.FC<Props> = ({ itinerary, cities }) => {
  const [activitySearches, setActivitySearches] = useState<
    ActivitySearch[] | []
  >([]);
  const [selectedSearch, setSelectedSearch] = useState<ActivitySearch | null>(
    null
  );
  const [offers, setOffers] = useState<Activity[] | []>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const createSearchList = () => {
    const searchList: ActivitySearch[] = [];

    if (!itinerary?.data?.hotel_cities) return;

    itinerary.data.hotel_cities.forEach((city, index) => {
      searchList.push({
        id: index,
        location: cities[city.location],
      });
    });

    setActivitySearches(searchList);
  };

  const fetchActivities = async (search: ActivitySearch) => {
    if (!search) return;

    setLoading(true);
    setError(null);
    setOffers([]);

    try {
      const activityParams = {
        language: "fr",
        currency: "EUR",
        lat: search.location.coordinates[0],
        lng: search.location.coordinates[1],
      };
      const res = await api.get(apiRoutes.search_activities, {
        params: activityParams,
      });

      setOffers(res.data.offers);
    } catch (err) {
      setError("Une erreur est survenue lors de la recherche de vols.");
    } finally {
      setLoading(false);
    }
  };

  const launchSearch = async (search: ActivitySearch) => {
    setSelectedSearch(search);
    if (search.location.coordinates) {
      fetchActivities(search);
    } else {
      setError("No coordinates for this city");
    }
  };

  const fallbackImage =
    "https://aws-tiqets-cdn.imgix.net/images/content/072a7e98d060434c9e94a9833e98b5c1.jpeg?auto=format%2Ccompress&fit=crop&ixlib=python-3.2.1&q=40&w=384&h=192";

  useEffect(() => {
    createSearchList();
  }, []);

  return (
    <div>
      <div className="d-flex h-100">
        {/* Menu latéral */}
        <div className="" style={{ width: "250px" }}>
          <ul className="list-group">
            {activitySearches &&
              activitySearches.map((search) => (
                <li
                  key={search.id}
                  className={`list-group-item list-group-item-action ${
                    selectedSearch?.id === search.id ? "active" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => launchSearch(search)}
                >
                  {search.location.cityname}
                </li>
              ))}
          </ul>
        </div>

        {/* Zone principale */}
        <div className="flex-grow-1 px-4 overflow-auto">
          {selectedSearch ? (
            <div className="border rounded p-3 bg-white shadow-sm">
              <h5>Activités à {selectedSearch.location.cityname}</h5>

              {loading && <LoadingMessage />}
              {error && <div className="text-danger">{error}</div>}

              {offers && (
                <Row xs={1} sm={2} md={3} lg={3} className="g-4">
                  {offers.map((activity, index) => (
                    <Col key={index}>
                      <Card className="h-100 shadow-sm">
                        <Card.Img
                          variant="top"
                          src={activity.image || fallbackImage}
                          alt={activity.title}
                          style={{ height: "200px", objectFit: "cover" }}
                        />
                        <Card.Body className="d-flex flex-column">
                          <Card.Title>{activity.title}</Card.Title>
                          <Card.Text className="flex-grow-1">
                            {activity.description}
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-end mt-3">
                            <div>
                              <strong>
                                {activity.price.toFixed(2)} {activity.currency}
                              </strong>
                            </div>
                            <Button
                              variant="primary"
                              href={activity.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Réserver
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          ) : (
            <p className="text-muted">
              Cliquez sur une recherche à gauche pour voir les résultats.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivitySearchPanel;
